import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import SafetyActions from "@/components/SafetyActions";
import { Search, UserPlus, Check, X, MessageCircle, Users, Clock } from "lucide-react";
import { initials } from "@/lib/format";
import { toast } from "sonner";

const Friends = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([api.get("/friends"), api.get("/friends/requests")]);
      setFriends(f.data.friends);
      setIncoming(r.data.incoming);
      setOutgoing(r.data.outgoing);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/users/search`, { params: { username: q.trim() } });
      setResults(data.results);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSearching(false);
    }
  };

  const sendRequest = async (username) => {
    try {
      await api.post("/friends/request", { username });
      toast.success("Demande envoyée");
      setResults((rs) => rs.map((r) => (r.username === username ? { ...r, relationship: "pending" } : r)));
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const respond = async (reqId, action) => {
    try {
      await api.post(`/friends/requests/${reqId}/${action}`);
      toast.success(action === "accept" ? "Ami ajouté" : "Demande refusée");
      loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl">Amis</h1>

      {/* Search */}
      <form onSubmit={doSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un pseudo…"
            className="pl-9 rounded-xl"
            data-testid="friends-search-input"
          />
        </div>
        <Button type="submit" className="rounded-xl" disabled={searching} data-testid="friends-search-button">
          Rechercher
        </Button>
      </form>

      {results.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Résultats</p>
          {results.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <Avatar className="h-9 w-9"><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(r.username)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">@{r.username}</p>
              </div>
              {r.relationship === "friend" ? (
                <Badge variant="secondary" className="gap-1"><Check className="h-3 w-3" /> Ami</Badge>
              ) : r.relationship === "pending" ? (
                <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> En attente</Badge>
              ) : (
                <Button size="sm" className="rounded-xl" onClick={() => sendRequest(r.username)} data-testid={`add-friend-${r.username}`}>
                  <UserPlus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="friends">
        <TabsList className="grid grid-cols-2 w-full rounded-xl">
          <TabsTrigger value="friends" data-testid="tab-friends-list">Mes amis ({friends.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Demandes {incoming.length > 0 && <Badge className="ml-1 h-5 px-1.5">{incoming.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="friends" className="mt-4 space-y-2">
          {loading ? (
            [0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)
          ) : friends.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2" />
              <p>Aucun ami pour le moment. Ajoutez quelqu'un par pseudo.</p>
            </div>
          ) : (
            friends.map((f) => (
              <div key={f.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(f.username)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><p className="font-medium truncate">@{f.username}</p></div>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate(`/messages/${f.username}`)} data-testid={`friends-open-conversation-${f.username}`}>
                  <MessageCircle className="h-4 w-4" />
                </Button>
                <SafetyActions targetUser={f} onBlocked={loadAll} />
              </div>
            ))
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-4 space-y-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Reçues</p>
            {incoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune demande en attente.</p>
            ) : (
              incoming.map((req) => (
                <div key={req.request_id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(req.user.username)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0"><p className="font-medium truncate">@{req.user.username}</p></div>
                  <Button size="sm" className="rounded-xl" onClick={() => respond(req.request_id, "accept")} data-testid={`friend-request-accept-${req.user.username}`}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => respond(req.request_id, "decline")} data-testid={`friend-request-decline-${req.user.username}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Envoyées</p>
            {outgoing.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune demande envoyée.</p>
            ) : (
              outgoing.map((req) => (
                <div key={req.request_id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(req.user.username)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0"><p className="font-medium truncate">@{req.user.username}</p></div>
                  <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> En attente</Badge>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Friends;
